/**
 * Utility functions for calculating and formatting parameter status
 */

import { removeCommasAndSpaces } from "./formatting-utils.js";
import {
  isEnalysisLowToHighBandUnitSetupParam,
  isEnalysisMinDegreesReversalBandParam,
  isEnalysisMinNetRodLoadRatioBandParam,
  isEnalysisRodLoadPercentBandParam,
  isEnalysisVolumeEfficiencyBandParam,
  isEnalysisZeroToHundredBandParam,
} from "@/constants/enalysis-trends-constants.js";

// Status object constants
const STATUS_NO_DATA = {
  label: "No Data",
  variant: "secondary",
  className: "bg-muted text-muted-foreground border-border",
};

const STATUS_NORMAL = {
  label: "Normal",
  variant: "secondary",
  className: "bg-green-50 text-green-800 border-green-200",
};

const STATUS_DANGER = {
  label: "Danger",
  variant: "destructive",
  className:
    "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30",
};

const STATUS_ALERTED = {
  label: "Alerted",
  variant: "outline",
  className: "bg-yellow-50 text-yellow-800 border-yellow-200",
};

/**
 * Calculate parameter status based on current value and alarm setpoints
 * @param {Object} param - Parameter object with currentValue, alarmSetPoints, status, displayType
 * @returns {Object} Status object with label, variant, and className
 */
export function getParameterStatus(param) {
  // Check for NoData status first
  if (param.status === "NoData") {
    return STATUS_NO_DATA;
  }

  if (isEnalysisZeroToHundredBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    if (currentValue >= 0 && currentValue <= 100) {
      return STATUS_NORMAL;
    }
    return STATUS_DANGER;
  }

  if (isEnalysisVolumeEfficiencyBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    if (currentValue < 12 || currentValue > 100) {
      return STATUS_DANGER;
    }
    if (currentValue < 20) {
      return STATUS_ALERTED;
    }
    return STATUS_NORMAL;
  }

  if (isEnalysisRodLoadPercentBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    if (currentValue < 0 || currentValue >= 99.9) {
      return STATUS_DANGER;
    }
    if (currentValue > 95) {
      return STATUS_ALERTED;
    }
    return STATUS_NORMAL;
  }

  if (isEnalysisMinDegreesReversalBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    if (currentValue < 0 || currentValue > 180 || currentValue <= 60) {
      return STATUS_DANGER;
    }
    if (currentValue < 70) {
      return STATUS_ALERTED;
    }
    return STATUS_NORMAL;
  }

  if (isEnalysisMinNetRodLoadRatioBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    if (currentValue < 0 || currentValue > 100 || currentValue <= 35) {
      return STATUS_DANGER;
    }
    if (currentValue < 40) {
      return STATUS_ALERTED;
    }
    return STATUS_NORMAL;
  }

  if (isEnalysisLowToHighBandUnitSetupParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return STATUS_NO_DATA;
      }
      return STATUS_NORMAL;
    }
    const hihi = parseFloat(param.alarmSetPoints?.hihi);
    const hi = parseFloat(param.alarmSetPoints?.hi);
    if (Number.isFinite(hihi) && currentValue >= hihi) {
      return STATUS_DANGER;
    }
    if (Number.isFinite(hi) && currentValue >= hi) {
      return STATUS_ALERTED;
    }
    return STATUS_NORMAL;
  }

  // For Text display types, show as Normal
  if (param.displayType === "Text") {
    return STATUS_NORMAL;
  }

  // Parse current value
  const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));

  // If value is invalid, check if it's a NoData scenario
  if (isNaN(currentValue) || !isFinite(currentValue)) {
    // Check if the currentValue string contains "No Data" indication
    const currentValueStr = String(param.currentValue).toLowerCase();
    if (
      currentValueStr.includes("no data")
      || currentValueStr.includes("nodata")
    ) {
      return STATUS_NO_DATA;
    }
    return STATUS_NORMAL;
  }

  const { lolo, lo, hi, hihi } = param.alarmSetPoints;
  const hihiN = parseFloat(hihi);
  const loloN = parseFloat(lolo);
  const hiN = parseFloat(hi);
  const loN = parseFloat(lo);

  // Only compare when setpoints are finite — null/undefined coerce badly (e.g. `v >= null` is `v >= 0`).
  if (Number.isFinite(hihiN) && currentValue >= hihiN) {
    return STATUS_DANGER;
  }
  if (Number.isFinite(loloN) && currentValue <= loloN) {
    return STATUS_DANGER;
  }
  if (Number.isFinite(hiN) && currentValue >= hiN) {
    return STATUS_ALERTED;
  }
  if (Number.isFinite(loN) && currentValue <= loN) {
    return STATUS_ALERTED;
  }

  return STATUS_NORMAL;
}

/**
 * Format parameter value for display
 * @param {Object} param - Parameter object with currentValue and status
 * @returns {string|number} Formatted value (returns raw number for further formatting if needed)
 */
export function formatParameterValue(param) {
  if (param.status === "NoData") {
    return "No Data";
  }
  if (typeof param.currentValue === "string") {
    return param.currentValue;
  }
  if (param.displayType === "Text") {
    return param.currentValue;
  }
  // Return number for further formatting
  return param.currentValue;
}

/**
 * Check if a parameter matches a given status filter
 * @param {Object} param - Parameter object
 * @param {string} statusFilter - Status filter ("all", "danger", "alerted", "normal", "nodata")
 * @returns {boolean} True if parameter matches the filter
 */
export function matchesStatusFilter(param, statusFilter) {
  if (statusFilter === "all") {
    return true;
  }

  if (param.status === "NoData") {
    return statusFilter === "nodata";
  }

  if (isEnalysisZeroToHundredBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    if (statusFilter === "danger") {
      return currentValue < 0 || currentValue > 100;
    }
    if (statusFilter === "alerted") {
      return false;
    }
    if (statusFilter === "normal") {
      return currentValue >= 0 && currentValue <= 100;
    }
    return false;
  }

  if (isEnalysisVolumeEfficiencyBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    if (statusFilter === "danger") {
      return currentValue < 12 || currentValue > 100;
    }
    if (statusFilter === "alerted") {
      return currentValue >= 12 && currentValue < 20;
    }
    if (statusFilter === "normal") {
      return currentValue >= 20 && currentValue <= 100;
    }
    return false;
  }

  if (isEnalysisRodLoadPercentBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    if (statusFilter === "danger") {
      return currentValue < 0 || currentValue >= 99.9;
    }
    if (statusFilter === "alerted") {
      return currentValue > 95 && currentValue < 99.9;
    }
    if (statusFilter === "normal") {
      return currentValue >= 0 && currentValue <= 95;
    }
    return false;
  }

  if (isEnalysisMinDegreesReversalBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    if (statusFilter === "danger") {
      return currentValue < 0 || currentValue > 180 || currentValue <= 60;
    }
    if (statusFilter === "alerted") {
      return currentValue > 60 && currentValue < 70;
    }
    if (statusFilter === "normal") {
      return currentValue >= 70 && currentValue <= 180;
    }
    return false;
  }

  if (isEnalysisMinNetRodLoadRatioBandParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    if (statusFilter === "danger") {
      return currentValue < 0 || currentValue > 100 || currentValue <= 35;
    }
    if (statusFilter === "alerted") {
      return currentValue > 35 && currentValue < 40;
    }
    if (statusFilter === "normal") {
      return currentValue >= 40 && currentValue <= 100;
    }
    return false;
  }

  if (isEnalysisLowToHighBandUnitSetupParam(param)) {
    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        return statusFilter === "nodata";
      }
      return statusFilter === "normal";
    }
    const hihi = parseFloat(param.alarmSetPoints?.hihi);
    const hi = parseFloat(param.alarmSetPoints?.hi);
    if (statusFilter === "danger") {
      return Number.isFinite(hihi) && currentValue >= hihi;
    }
    if (statusFilter === "alerted") {
      return (
        Number.isFinite(hi)
        && currentValue >= hi
        && !(Number.isFinite(hihi) && currentValue >= hihi)
      );
    }
    if (statusFilter === "normal") {
      return !(Number.isFinite(hi) && currentValue >= hi);
    }
    return false;
  }

  if (param.displayType === "Text") {
    return statusFilter === "normal";
  }

  const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));

  if (isNaN(currentValue) || !isFinite(currentValue)) {
    // Check if it's actually a NoData scenario
    const currentValueStr = String(param.currentValue).toLowerCase();
    if (
      currentValueStr.includes("no data")
      || currentValueStr.includes("nodata")
    ) {
      return statusFilter === "nodata";
    }
    return statusFilter === "normal";
  }

  const { lolo, lo, hi, hihi } = param.alarmSetPoints;
  const hihiN = parseFloat(hihi);
  const loloN = parseFloat(lolo);
  const hiN = parseFloat(hi);
  const loN = parseFloat(lo);

  const inDanger =
    (Number.isFinite(hihiN) && currentValue >= hihiN)
    || (Number.isFinite(loloN) && currentValue <= loloN);
  const inAlerted =
    (Number.isFinite(hiN) && currentValue >= hiN)
    || (Number.isFinite(loN) && currentValue <= loN);

  if (statusFilter === "danger") {
    return inDanger;
  }

  if (statusFilter === "alerted") {
    return inAlerted && !inDanger;
  }

  if (statusFilter === "normal") {
    const aboveLo = !Number.isFinite(loN) || currentValue > loN;
    const belowHi = !Number.isFinite(hiN) || currentValue < hiN;
    const aboveLolo = !Number.isFinite(loloN) || currentValue > loloN;
    const belowHihi = !Number.isFinite(hihiN) || currentValue < hihiN;
    return aboveLo && belowHi && aboveLolo && belowHihi;
  }

  if (statusFilter === "nodata") {
    return false; // NoData already handled above
  }

  return false;
}

/**
 * Count parameters by status
 * @param {Array} parameters - Array of parameter objects
 * @returns {Object} Counts object with all, danger, alerted, normal, nodata
 */
export function countParametersByStatus(parameters) {
  let all = 0;
  let danger = 0;
  let alerted = 0;
  let normal = 0;
  let nodata = 0;

  parameters.forEach((param) => {
    all++;
    if (param.status === "NoData") {
      nodata++;
      return;
    }
    if (isEnalysisZeroToHundredBandParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      if (currentValue < 0 || currentValue > 100) {
        danger++;
      } else {
        normal++;
      }
      return;
    }
    if (isEnalysisVolumeEfficiencyBandParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      if (currentValue < 12 || currentValue > 100) {
        danger++;
      } else if (currentValue < 20) {
        alerted++;
      } else {
        normal++;
      }
      return;
    }
    if (isEnalysisRodLoadPercentBandParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      if (currentValue < 0 || currentValue >= 99.9) {
        danger++;
      } else if (currentValue > 95) {
        alerted++;
      } else {
        normal++;
      }
      return;
    }
    if (isEnalysisMinDegreesReversalBandParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      if (currentValue < 0 || currentValue > 180 || currentValue <= 60) {
        danger++;
      } else if (currentValue < 70) {
        alerted++;
      } else {
        normal++;
      }
      return;
    }
    if (isEnalysisMinNetRodLoadRatioBandParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      if (currentValue < 0 || currentValue > 100 || currentValue <= 35) {
        danger++;
      } else if (currentValue < 40) {
        alerted++;
      } else {
        normal++;
      }
      return;
    }
    if (isEnalysisLowToHighBandUnitSetupParam(param)) {
      const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
      if (Number.isNaN(currentValue) || !Number.isFinite(currentValue)) {
        const currentValueStr = String(param.currentValue).toLowerCase();
        if (
          currentValueStr.includes("no data")
          || currentValueStr.includes("nodata")
        ) {
          nodata++;
        } else {
          normal++;
        }
        return;
      }
      const hihi = parseFloat(param.alarmSetPoints?.hihi);
      const hi = parseFloat(param.alarmSetPoints?.hi);
      if (Number.isFinite(hihi) && currentValue >= hihi) {
        danger++;
      } else if (Number.isFinite(hi) && currentValue >= hi) {
        alerted++;
      } else {
        normal++;
      }
      return;
    }
    if (param.displayType === "Text") {
      normal++;
      return;
    }

    const currentValue = parseFloat(removeCommasAndSpaces(param.currentValue));
    if (isNaN(currentValue) || !isFinite(currentValue)) {
      // Check if it's actually a NoData scenario
      const currentValueStr = String(param.currentValue).toLowerCase();
      if (
        currentValueStr.includes("no data")
        || currentValueStr.includes("nodata")
      ) {
        nodata++;
      } else {
        normal++;
      }
      return;
    }

    const { lolo, lo, hi, hihi } = param.alarmSetPoints;
    const hihiN = parseFloat(hihi);
    const loloN = parseFloat(lolo);
    const hiN = parseFloat(hi);
    const loN = parseFloat(lo);

    const inDanger =
      (Number.isFinite(hihiN) && currentValue >= hihiN)
      || (Number.isFinite(loloN) && currentValue <= loloN);
    const inAlerted =
      (Number.isFinite(hiN) && currentValue >= hiN)
      || (Number.isFinite(loN) && currentValue <= loN);

    if (inDanger) {
      danger++;
    } else if (inAlerted) {
      alerted++;
    } else {
      normal++;
    }
  });

  return { all, danger, alerted, normal, nodata };
}
